import {
  forwardRef, useEffect, useId, useRef, useState,
  type HTMLAttributes, type ReactElement, type ReactNode, type TdHTMLAttributes, type ThHTMLAttributes, type TableHTMLAttributes,
} from 'react'
import { cn } from '@/lib/utils'
import './table.css'

/**
 * How a cell's content reads: prose; an identifier, address or time in the data font; a number in the data font,
 * aligned to the end; or a row's actions, aligned to the end.
 */
export type TableCellType = 'text' | 'data' | 'number' | 'actions'

export type TableProps = Omit<TableHTMLAttributes<HTMLTableElement>, 'children'> & {
  /** Names the table. Shown above it unless hidden. */
  caption: ReactNode
  /** Hide the caption visually when a nearby heading already names the table. It is still read. */
  hideCaption?: boolean
  children: ReactNode
}

/** True while the element's content is wider than the element, so it scrolls horizontally. */
function useHorizontalOverflow<T extends HTMLElement>() {
  const ref = useRef<T>(null)
  const [overflowing, setOverflowing] = useState(false)
  useEffect(() => {
    const element = ref.current
    if (!element) return
    const update = () => setOverflowing(element.scrollWidth > element.clientWidth)
    update()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(update)
    observer.observe(element)
    if (element.firstElementChild) observer.observe(element.firstElementChild)
    return () => observer.disconnect()
  }, [])
  return [ref, overflowing] as const
}

/** Rows of records to compare and act on. Scrolls sideways on small screens instead of squeezing columns. */
export const Table = forwardRef<HTMLTableElement, TableProps>(function Table(
  { caption, hideCaption = false, className, children, ...props },
  ref,
) {
  const captionId = useId()
  const [scrollRef, overflowing] = useHorizontalOverflow<HTMLDivElement>()
  // When the table is wider than its container, the wrapper becomes a named, focusable region so keyboard users
  // can scroll it. Otherwise it stays a plain box and adds no tab stop.
  return (
    <div
      ref={scrollRef}
      className="play-table"
      role={overflowing ? 'region' : undefined}
      aria-labelledby={overflowing ? captionId : undefined}
      tabIndex={overflowing ? 0 : undefined}
    >
      <table ref={ref} className={cn('play-table__table', className)} {...props}>
        <caption id={captionId} className={hideCaption ? 'sr-only' : 'play-table__caption'}>{caption}</caption>
        {children}
      </table>
    </div>
  )
})

export const TableHeader = forwardRef<HTMLTableSectionElement, HTMLAttributes<HTMLTableSectionElement>>(function TableHeader(
  { className, ...props },
  ref,
) {
  return <thead ref={ref} className={cn('play-table__header', className)} {...props} />
})

export const TableBody = forwardRef<HTMLTableSectionElement, HTMLAttributes<HTMLTableSectionElement>>(function TableBody(
  { className, ...props },
  ref,
) {
  return <tbody ref={ref} className={cn('play-table__body', className)} {...props} />
})

export const TableRow = forwardRef<HTMLTableRowElement, HTMLAttributes<HTMLTableRowElement>>(function TableRow(
  { className, ...props },
  ref,
) {
  return <tr ref={ref} className={cn('play-table__row', className)} {...props} />
})

const cellClass = (base: string, type: TableCellType, className?: string) =>
  cn(base, type !== 'text' && `play-table__cell--${type}`, className)

export type TableHeaderCellProps = ThHTMLAttributes<HTMLTableCellElement> & {
  /** Match the column's cells: `number` and `actions` align the heading to the end. */
  type?: TableCellType
}

/** A column (or row) heading. Defaults to `scope="col"`. Give action columns a visually hidden name. */
export const TableHeaderCell = forwardRef<HTMLTableCellElement, TableHeaderCellProps>(function TableHeaderCell(
  { type = 'text', scope = 'col', className, ...props },
  ref,
) {
  return <th ref={ref} scope={scope} className={cellClass('play-table__header-cell', type, className)} {...props} />
})

export type TableCellProps = TdHTMLAttributes<HTMLTableCellElement> & {
  /** `data` for identifiers, addresses and times; `number` for counts and sizes; `actions` for a row's buttons. */
  type?: TableCellType
}

export const TableCell = forwardRef<HTMLTableCellElement, TableCellProps>(function TableCell(
  { type = 'text', className, ...props },
  ref,
) {
  return <td ref={ref} className={cellClass('play-table__cell', type, className)} {...props} />
})

export type TableEmptyProps = {
  /** The number of columns, so the message spans the table. */
  colSpan: number
  /** A Lucide icon that echoes what is missing. */
  icon?: ReactElement
  /** What is missing, and the next step when there is one. */
  children: ReactNode
}

/** A full-width row for an empty table or a filter with no matches. */
export function TableEmpty({ colSpan, icon, children }: TableEmptyProps) {
  return (
    <tr className="play-table__empty">
      <td colSpan={colSpan}>
        <div className="play-table__empty-content">
          {icon && <span className="play-table__empty-icon" aria-hidden="true">{icon}</span>}
          {children}
        </div>
      </td>
    </tr>
  )
}
